import type { InputHTMLAttributes } from "react";

import { cx } from "@/lib/cx";

/**
 * DESIGN.md § Components `input`: 44px, `border-strong`, `rounded.md`,
 * `typography.body` (16px, which also prevents iOS zoom-on-focus). Focus
 * swaps the border to `{colors.accent}` on top of the global focus ring
 * (`index.css`'s `:focus` rule) — the border-swap and the ring are two
 * distinct visual cues DESIGN.md names separately.
 *
 * `invalid`: DESIGN.md's own error-state spec — "{colors.danger} border
 * plus a {typography.meta} message in {colors.danger} beneath — never a red
 * fill, never an icon alone." This swaps the border only; the caller still
 * owns rendering the message itself (and should point `aria-describedby` at
 * it, since this component doesn't know what that message's id is).
 */
export function Input({
  className,
  invalid,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      aria-invalid={invalid ? true : undefined}
      className={cx(
        "h-tap-target-min w-full rounded-md border bg-surface px-3 text-body text-ink placeholder-ink-tertiary focus:border-border-focus",
        invalid ? "border-danger" : "border-border-input",
        className,
      )}
      {...props}
    />
  );
}
